import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import Handlebars from 'handlebars';
import root, { Root } from 'react-shadow';
import { useGetBlocksQuery } from '../../../services/blocks.service';
import { ProjectWorkbookUploadEventBus } from '../../../events/project-workbook-upload';
import { useFormContext } from 'react-hook-form';

const ShadowRoot = root as Record<'div', Root[string]>;

interface Props {
  fields: Record<string, string>[];
  data: OrderedListDynamicFormField;
  prefix: string;
  append: (value: any) => void;
  remove: (index: number) => void;
  swap: (indexA: number, indexB: number) => void;
  InputRenderer: React.FC<{
    data: DynamicFormField;
    prefix?: string;
    queryGqlData?: (query: string) => Promise<Record<string, any>>;
  }>;
  queryGqlData?: (query: string) => Promise<Record<string, any>>;
}

const ItemsListContainer = styled.div`
  display: grid;
  grid-template-rows: max-content minmax(0, 1fr);

  .label {
    margin: 0 0 8px 0;
  }

  .label-secondary {
    font-size: 12px;
    color: #777;
    margin-left: 4px;
  }

  .items-list-scroller {
    height: 400px;
    overflow-y: auto;
  }

  .items-list {
    position: relative;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border: 1px solid #c3c3c3;
    border-radius: 8px;
  }

  .items-list-item.target {
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    gap: 8px;
    align-items: center;
  }

  .items-list-item {
    cursor: pointer;
    user-select: none;
  }

  .preloader {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }

  @keyframes spin {
    from {
      transform: translate(-50%, -50%) rotate(0deg);
    }
    to {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }

  .preloader .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  // .items-list table th {
  //   text-align: left;
  //   padding: 2px 8px;
  // }

  // .items-list table td {
  //   text-align: left;
  //   padding: 2px 8px;
  // }

  // .items-list tbody tr:hover {
  //   background-color: #eee;
  // }

  // .actions-column-order div {
  //   display: flex;
  //   align-items: center;
  //   justify-content: center;
  //   gap: 8px;
  // }
`;

const Container = styled.div<{ variant: string }>`
  display: grid;

  ${({ variant }) =>
    variant === 'default' &&
    `
    grid-template-rows: 1fr;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 8px;
  `}

  ${({ variant }) =>
    variant === 'column' &&
    `
    grid-template-rows: 400px 400px;
    grid-template-columns: 1fr;
    gap: 32px;
  `}
`;

const FilterInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #c3c3c3;
  border-radius: 4px;
  font-size: 14px;
  margin-bottom: 8px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &::placeholder {
    color: #999;
  }
`;

const PreviewPlaceholder = styled.div`
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #999;
  font-size: 14px;
`;

// Lazy preview component that only renders when visible
const LazyPreview: React.FC<{
  html: string;
  css: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}> = ({ html, css, className, onClick, scrollContainerRef }) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    const element = elementRef.current;
    const scrollContainer = scrollContainerRef?.current;

    if (!element) return;

    // Check if element is initially in viewport
    const checkVisibility = () => {
      if (!element || !isMountedRef.current) return false;

      const elementRect = element.getBoundingClientRect();
      const containerRect = scrollContainer ? scrollContainer.getBoundingClientRect() : {
        top: 0,
        bottom: window.innerHeight,
        left: 0,
        right: window.innerWidth,
      };

      const isInView = (
        elementRect.top < containerRect.bottom + 200 &&
        elementRect.bottom > containerRect.top - 200 &&
        elementRect.left < containerRect.right &&
        elementRect.right > containerRect.left
      );

      return isInView;
    };

    // Small delay to ensure layout is ready
    const timeoutId = setTimeout(() => {
      if (!isMountedRef.current) return;

      // Check immediately
      const isInitiallyVisible = checkVisibility();

      if (isInitiallyVisible) {
        setIsVisible(true);
      } else {
        // If not initially visible, set up intersection observer
        observerRef.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && isMountedRef.current) {
                setIsVisible(true);
                if (observerRef.current) {
                  observerRef.current.disconnect();
                }
              }
            });
          },
          {
            root: scrollContainer,
            rootMargin: '200px',
            threshold: 0,
          },
        );

        observerRef.current.observe(element);
      }
    }, 100);

    return () => {
      isMountedRef.current = false;
      clearTimeout(timeoutId);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollContainerRef]);

  return (
    <div ref={elementRef} onClick={onClick}>
      {isVisible ? (
        <ShadowRoot.div>
          <style>{css}</style>
          <style>
            {`
              .shadow-root-wrapper > * {
                width: 100%;
                max-width: 100%;
                box-sizing: border-box;
              }

              .shadow-root-wrapper.disabled {
                cursor: not-allowed;
                opacity: 0.5;
              }
            `}
          </style>
          <div dangerouslySetInnerHTML={{ __html: html }} className={className} />
        </ShadowRoot.div>
      ) : (
        <PreviewPlaceholder>Loading preview...</PreviewPlaceholder>
      )}
    </div>
  );
};

export const OrderedList: React.FC<Props> = ({
  fields,
  data: fieldData,
  prefix,
  InputRenderer,
  queryGqlData,
  append,
  remove,
  swap,
}) => {
  const { setError } = useFormContext();
  const [isLoading, setIsLoading] = useState(false);
  const [allSourceItems, setAllSourceItems] = useState<any[]>([]);
  const [sourceFilter, setSourceFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const sourceScrollRef = useRef<HTMLDivElement>(null);
  const targetScrollRef = useRef<HTMLDivElement>(null);

  const { data: blocks, isLoading: isBlocksLoading } = useGetBlocksQuery();

  const template = useMemo(() => {
    if (!blocks || blocks.length === 0) {
      return {
        html: () => '',
        css: '',
      };
    }

    for (const block of blocks ?? []) {
      if (block.html) {
        let html = block.html;
        if (block.blockStylesheet) {
          html += `<style>${block.blockStylesheet?.css}</style>`;
        }

        Handlebars.registerPartial(block.name, Handlebars.compile(html));
      }
    }

    const previewComponent = fieldData.inputOptions.previewComponent
      ? `{{> ${fieldData.inputOptions.previewComponent}}}`
      : `
      <div style="padding: 4px; display: flex; border: 1px solid black; flex-direction: column; gap: 4px;">
        ${fieldData.items
          .map(
            (item) =>
              `<div style="border-bottom: 1px solid black; padding-left: 8px;"><span style="font-weight: 600;">${item.name}:</span> {{${item.name}}}</div>`,
          )
          .join('\n')}
      </div>
    `;

    return {
      html: Handlebars.compile(previewComponent),
      css: '',
    };
  }, [blocks, fieldData.inputOptions.previewComponent]);

  // Helper function to filter items based on text search
  const filterItems = useCallback((items: any[], filterText: string) => {
    if (!filterText.trim()) return items;

    const searchTerm = filterText.toLowerCase();
    return items.filter((item) => {
      // Search through all string properties of the item
      return Object.values(item.item || item).some((value) => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchTerm);
        }
        return false;
      });
    });
  }, []);

  const allTargetItems = fields
    .map((f) => allSourceItems.find((sItem) => sItem['ID'] === f['ID']))
    .filter(Boolean)
    .map((item) => ({
      item,
      html: template.html(item),
    }));

  const allSourceItemsFormatted = allSourceItems
    .filter((sItem) => allTargetItems.some((tItem) => tItem.item['ID'] === sItem['ID']) === false)
    .map((item) => ({
      item,
      html: template.html(item),
    }));

  // Apply filters if text filtering is enabled
  const targetItems =
    fieldData.inputOptions.filterType === 'text' ? filterItems(allTargetItems, targetFilter) : allTargetItems;

  const sourceItems =
    fieldData.inputOptions.filterType === 'text'
      ? filterItems(allSourceItemsFormatted, sourceFilter)
      : allSourceItemsFormatted;

  const reachedSelectionLimit = typeof fieldData.maxItems === 'number' && allTargetItems.length >= fieldData.maxItems;

  const loadResources = useCallback(async () => {
    if (!queryGqlData || !fieldData.inputOptions.gqlQuery) {
      return;
    }

    setIsLoading(true);
    const response: any = await queryGqlData(fieldData.inputOptions.gqlQuery);

    if (response?.errors?.length) {
      const msg = response?.errors[0]?.message;

      setError(`${prefix}.${fieldData.name}`, {
        type: 'manual',
        message: msg,
      });

      setIsLoading(false);
      return;
    }

    const queryKey = (fieldData.inputOptions.gqlQueryKey ?? fieldData.name).trim();
    const items = response.data?.[queryKey] ?? [];

    setAllSourceItems(items);
    setIsLoading(false);
  }, [fieldData.inputOptions.gqlQuery]);

  const onAppend = (item?: any) => {
    if (item) {
      append({ ID: item.item.ID });
    } else {
      const item = sourceItems[sourceItems.length - 1];
      if (!item) return;
      append({ ID: item.item.ID });
    }
  };

  const onRemove = (index?: number) => {
    remove(index ?? targetItems.length - 1);
  };

  const onSwap = (indexA: number, indexB: number) => {
    swap(indexA, indexB);
  };

  useEffect(() => {
    loadResources();

    return ProjectWorkbookUploadEventBus.subscribe('@clatter/PROJECT_WORKBOOK_UPLOAD_COMPLETED', loadResources);
  }, [loadResources]);

  // Reset scroll positions when component mounts or when fieldData.name changes (tab switch)
  useEffect(() => {
    if (sourceScrollRef.current) {
      sourceScrollRef.current.scrollTop = 0;
    }
    if (targetScrollRef.current) {
      targetScrollRef.current.scrollTop = 0;
    }
  }, [fieldData.name]);

  return (
    <Container variant={fieldData.inputOptions.variant}>
      <ItemsListContainer>
        <p className="label">
          <span>Source</span>
          <span className="label-secondary">
            (
            {fieldData.inputOptions.filterType === 'text' && sourceFilter
              ? `${sourceItems.length} filtered, ${allSourceItemsFormatted.length}`
              : sourceItems.length}{' '}
            available)
          </span>
        </p>

        {fieldData.inputOptions.filterType === 'text' && (
          <FilterInput
            type="text"
            placeholder="Filter source items..."
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          />
        )}

        <div className="items-list-scroller" ref={sourceScrollRef}>
          <div className="items-list">
            {isLoading && (
              <div className="preloader">
                <div className="spinner"></div>
              </div>
            )}

            {sourceItems.map((item, idx) => (
              <div key={item.item['_id']} className="items-list-item">
                <LazyPreview
                  html={item.html}
                  css={template.css}
                  className={`shadow-root-wrapper ${reachedSelectionLimit ? 'disabled' : ''}`}
                  scrollContainerRef={sourceScrollRef as any}
                  onClick={(e) => {
                    if (reachedSelectionLimit) return;

                    e.stopPropagation();
                    e.preventDefault();

                    onAppend(item);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </ItemsListContainer>
      <ItemsListContainer>
        <p className="label">
          <span>Target</span>
          <span className="label-secondary">
            (
            {fieldData.inputOptions.filterType === 'text' && targetFilter
              ? `${targetItems.length} filtered, ${allTargetItems.length}`
              : allTargetItems.length}{' '}
            / {fieldData.maxItems ?? '∞'} selected
            {fieldData.minItems !== undefined ? `, min ${fieldData.minItems} required` : ''})
          </span>
        </p>

        {fieldData.inputOptions.filterType === 'text' && (
          <FilterInput
            type="text"
            placeholder="Filter target items..."
            value={targetFilter}
            onChange={(e) => setTargetFilter(e.target.value)}
          />
        )}

        <div className="items-list-scroller" ref={targetScrollRef}>
          <div className="items-list">
            {targetItems.map((item, idx) => (
              <div key={item.item['ID']} className="items-list-item target">
                <LazyPreview
                  html={item.html}
                  css={template.css}
                  className="shadow-root-wrapper"
                  scrollContainerRef={targetScrollRef as any}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onRemove(idx);
                  }}
                />
                <div>
                  <button type="button" onClick={() => onSwap(idx, idx - 1)} disabled={idx === 0}>
                    ↑
                  </button>
                  <button type="button" onClick={() => onSwap(idx, idx + 1)} disabled={idx === targetItems.length - 1}>
                    ↓
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ItemsListContainer>
    </Container>
  );
};
